import { useEffect, useState } from 'react';
import { SITE } from '@/content/site';
import { TID } from '@/constants/testIds';
import { Menu, X } from 'lucide-react';

const NAV = [
  { k: 'treatments', label: 'Treatments', href: '#treatments' },
  { k: 'process', label: 'Process', href: '#process' },
  { k: 'pricing', label: 'Pricing', href: '#pricing' },
  { k: 'team', label: 'Team', href: '#team' },
  { k: 'gallery', label: 'Gallery', href: '#gallery' },
  { k: 'faq', label: 'FAQ', href: '#faq' },
  { k: 'contact', label: 'Contact', href: '#contact' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`transition-all duration-[700ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
        scrolled
          ? 'bg-[hsl(var(--background))]/85 backdrop-blur-xl backdrop-saturate-150 border-b border-[hsl(var(--hairline))]'
          : 'bg-[hsl(var(--background))]/55 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 lg:h-[72px] flex items-center justify-between">
        <a
          data-testid={TID.navLogo}
          href="#top"
          className="group flex items-center gap-2.5"
          aria-label="Lash Lift Vancouver — back to top"
        >
          <span
            className="font-display-xl text-[1.5rem] lg:text-[1.7rem] leading-none text-[hsl(var(--primary))] italic"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
          >
            Lash Lift
          </span>
          <span className="inline-block w-px h-5 bg-[hsl(var(--hairline))]" aria-hidden />
          <span className="text-[0.65rem] tracking-[0.32em] uppercase text-[hsl(var(--muted-foreground))] font-light">
            Vancouver
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((item) => (
            <a
              key={item.k}
              data-testid={TID.navLink(item.k)}
              href={item.href}
              className="text-[0.78rem] tracking-[0.18em] uppercase font-light text-[hsl(var(--foreground))]/65 hover:text-[hsl(var(--primary))] transition-colors duration-300"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid={TID.headerBookBtn}
            href={SITE.booking}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-luxe hidden sm:inline-flex items-center rounded-full bg-[hsl(var(--warm-ink))] text-[hsl(var(--cream))] px-6 lg:px-7 py-2.5 text-[0.72rem] tracking-[0.22em] uppercase font-light hover:bg-[hsl(var(--primary))] transition-colors duration-500"
          >
            Book Now
          </a>
          <button
            type="button"
            data-testid={TID.navMobileToggle}
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[hsl(var(--hairline))] text-[hsl(var(--foreground))]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-4 h-4" strokeWidth={1.25} /> : <Menu className="w-4 h-4" strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[hsl(var(--hairline))] bg-[hsl(var(--background))]/95 backdrop-blur-xl">
          <nav className="max-w-7xl mx-auto px-6 py-7 flex flex-col gap-5">
            {NAV.map((item) => (
              <a
                key={item.k}
                data-testid={`${TID.navLink(item.k)}-mobile`}
                href={item.href}
                onClick={() => setOpen(false)}
                className="text-base tracking-[0.04em] text-[hsl(var(--foreground))]/85"
              >
                {item.label}
              </a>
            ))}
            <a
              href={SITE.booking}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center justify-center rounded-full bg-[hsl(var(--warm-ink))] text-[hsl(var(--cream))] px-7 py-3 text-xs tracking-[0.22em] uppercase font-light"
            >
              Book Now
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
